import { Injectable, signal } from '@angular/core';
import type { MessageSeverity } from '../message/hh-message';

export type HhToast = { severity: MessageSeverity; text: string };

/** How long each severity stays up unless a duration is passed. Errors
 * linger because they usually ask the player to do something. */
const DEFAULT_MS: Partial<Record<MessageSeverity, number>> = { error: 8_000 };
const FALLBACK_MS = 3_000;

/**
 * App-wide toast state. One toast at a time — a new one replaces
 * whatever is showing rather than queueing behind it, since a stale
 * "Reconnected" is worth nothing once the next thing has happened.
 *
 * Rendered by a single <hh-toaster /> near the app root, so a toast
 * survives route changes.
 */
@Injectable({ providedIn: 'root' })
export class HhToastService {
  private readonly _current = signal<HhToast | null>(null);
  readonly current = this._current.asReadonly();

  private timer: ReturnType<typeof setTimeout> | null = null;

  show(severity: MessageSeverity, text: string, durationMs?: number) {
    this.dismiss();
    this._current.set({ severity, text });
    this.timer = setTimeout(
      () => this.dismiss(),
      durationMs ?? DEFAULT_MS[severity] ?? FALLBACK_MS,
    );
  }

  success(text: string, durationMs?: number) {
    this.show('success', text, durationMs);
  }

  error(text: string, durationMs?: number) {
    this.show('error', text, durationMs);
  }

  dismiss() {
    if (this.timer !== null) clearTimeout(this.timer);
    this.timer = null;
    this._current.set(null);
  }
}
